import { emojisRegex } from '../md/plugins/emoji';
import { escapeCode } from '../md/md-utils';
import { setSelection } from './helpers';
import { createEmojiElement } from './html';
import { mentionRegex, getMentionById, getMentionBySymbol } from '../md/plugins/mention';

export const SPECIAL_MENTIONS = ['everyone', 'here'];

export const getInputAfterTrigger = triggerChar => {
    const selection = window.getSelection();
    const { focusNode, focusOffset, isCollapsed } = selection;
    
    if (isCollapsed && focusNode?.textContent) {
        const { textContent } = focusNode;

        if (!(new RegExp(`(\\s|^|\u200B)${triggerChar}`).test(textContent))) {
            return null;
        }

        const charBefore = textContent[focusOffset - 1];

        if (charBefore === triggerChar) {
            return '';
        }

        const beforeText = textContent.slice(0, focusOffset).split(triggerChar).pop();

        if (beforeText) {
            return beforeText.replace(/\s/g, ' ');
        }

        return null;
    }
}

export const toggleMd = ({ symbolBefore = '', symbolAfter = '', increment, fullLine, wrapBlock }) => {
    const countBefore = symbolBefore.length;
    const countAfter = symbolAfter.length;
    const selectedText = getSelectedText(countBefore, countAfter);

    if (!selectedText) {
        return;
    }

    const matchText = testMdText(selectedText.text, countBefore, countAfter, symbolBefore, symbolAfter);
    const matchExtended = testMdText(selectedText.extended, countBefore, countAfter, symbolBefore, symbolAfter);

    if (matchText || matchExtended) {
        unwrapAtRange(selectedText.node, {
            start: selectedText.start,
            end: selectedText.end
        }, countBefore, countAfter);

        setSelection({
            node: selectedText.node,
            offset: selectedText.start - countBefore
        }, {
            node: selectedText.node,
            offset: selectedText.end - countBefore
        });
    } else {
        let start = selectedText.start;
        let end = selectedText.end;
        const text = selectedText.nodeText;

        if (fullLine) {
            if (start > 0) {
                if (start - 1 !== 0 && text[start - 1] !== '\n') {
                    while (start !== 0 && text[start - 1] !== '\n') {
                        start -= 1;
                    }
                }
            }

            if (text[end + 1] !== undefined && text[end] !== '\n') {
                if (end + 1 !== 0 && text[end + 1] !== '\n') {
                    while (text[end + 1] !== undefined && text[end] !== '\n') {
                        end += 1;
                    }
                }
            }
        }

        const lines = text.slice(start, end).split('\n');
        const multiLine = lines.length > 1;

        if (!wrapBlock && (multiLine || fullLine)) {
            let last = start || 0;

            lines.forEach((line, index) => {
                if (!line.length) {
                    last += 1;
                    return;
                }

                if (increment) {
                    symbolBefore = symbolBefore.replace(/\d+/, index + 1);
                }

                const trimmedLine = line.trim();
                const trimmedStart = line.indexOf(trimmedLine);

                if (trimmedLine.indexOf(symbolBefore) === 0 && trimmedLine.lastIndexOf(symbolAfter) >= trimmedLine.length - 1) {
                    last += line.length + 1;
                    return;
                }

                const rangeStart = last + trimmedStart
                const rangeEnd = last + trimmedStart + trimmedLine.length;

                last = last + line.length + countBefore + countAfter + 1;

                wrapAtRange(selectedText.node, {
                    start: rangeStart,
                    end: rangeEnd
                }, symbolBefore, symbolAfter)
            })

        } else {
            if (wrapBlock) {
                symbolBefore += '\n';
                symbolAfter = '\n' + symbolAfter;
            }

            wrapAtRange(selectedText.node, {
                start,
                end
            }, symbolBefore, symbolAfter);

            setSelection({
                node: selectedText.node,
                offset: start + symbolBefore.length
            }, {
                node: selectedText.node,
                offset: end + symbolBefore.length
            });
        }
    }
}

export const deserialize = (source, mentionUsers, readableFormat) => {
    try {
        return escapeCode(source, escapedSource => {
            return convertCustomNodes(escapedSource, mentionUsers, readableFormat);
        });
    } catch (e) {
        return source;
    }
}

const getSelectedText = (extendedByBefore, extendedByAfter) => {
    const selection = window.getSelection();

    if (!selection) {
        return null;
    }

    const selectionAnchor = selection.anchorOffset;
    const selectionFocus = selection.focusOffset;

    const start = Math.min(selectionAnchor, selectionFocus);
    let end = Math.max(selectionAnchor, selectionFocus);
    const node = selection.anchorNode;
    const nodeText = node.textContent;

    if (nodeText[end - 1] === '\n') {
        end -= 1;
    }

    return {
        node,
        nodeText,
        originalSelection: selection,
        start,
        end,
        text: nodeText.slice(start, end),
        extended: nodeText.slice(start - extendedByBefore, end + extendedByAfter)
    };
}

const testMdText = (text, countBefore, countAfter, matchBefore, matchAfter) => {
    return text.slice(0, countBefore) === matchBefore && text.slice(-countAfter) === matchAfter;
};

export const insertTextAtPosition = (source, text, position) => {
    return [source.slice(0, position), text, source.slice(position)].join('');
}

const removeTextAfter = (source, start, count) => {
    return [source.slice(0, start), source.slice(start + count)].join('');
}

const removeTextBefore = (source, start, count) => {
    return [source.slice(0, start - count), source.slice(start)].join('');
}

const wrapAtRange = (node, range, before = '', after = '') => {
    let nodeText = node.textContent;

    if (after) {
        nodeText = insertTextAtPosition(nodeText, after, range.end);
    }

    if (before) {
        nodeText = insertTextAtPosition(nodeText, before, range.start);
    }

    node.textContent = nodeText;
}

const unwrapAtRange = (node, range, before = 0, after = 0) => {
    let nodeText = node.textContent;

    if (after) {
        nodeText = removeTextAfter(nodeText, range.end, after);
    }

    if (before) {
        nodeText = removeTextBefore(nodeText, range.start, before);
    }

    node.textContent = nodeText;
}

const convertCustomNodes = (markdownSource, mentionUsers, readableFormat) => {
    const markdownUrls = [];

    if (typeof markdownSource !== 'string') {
        return markdownSource;
    }

    let md = markdownSource.replace(/([])|\[(.*?)\]\(.*?\)/g, markdownUrl => {
        markdownUrls.push(markdownUrl);

        return '%NIFTY_MARKDOWN_URL_REPLACER%';
    });

    md = md
        .replace(RegExp(mentionRegex, 'g'), (match, symbol, id) => {
            const mentionType = getMentionBySymbol(symbol);

            if (mentionType.id === 'user' && readableFormat && mentionUsers.length) {
                const user = mentionUsers.find(u => u.id === id);

                if (user) {
                    return `@${user.name || user.email}`;
                }
            }

            if (mentionType) {
                return `<span data-type="${mentionType.id}" data-id="${id}">@${id}</span>`;
            }

            return match;
        });

    md = md.replace(RegExp(emojisRegex, 'g'), match => {
        if (readableFormat) {
            return match;
        }

        return createEmojiElement(match).outerHTML;
    });
    md = md.replace(/%NIFTY_MARKDOWN_URL_REPLACER%/g, match => markdownUrls.shift());

    return md;
}
